import React, { useState, useEffect } from "react";
import Loader from "react-loader";
import { InfoModal, ErrorModal } from "./AppModal";
import { doAuthorization } from "./utils/authorize";
import { get, post } from "./utils/fetch";
import {
  API_ENDPOINT,
  APP_ENDPOINT,
  CLIENT_ID,
  DIRECTORY_SCOPES,
  SI_API_ENDPOINT,
  VALID_SERVICE_CLASS,
} from "./utils/constants";
import { parse } from "querystring";
import VerifyForm from "./components/pages/VerifyForm";

function Verify() {
  const {
    accessToken,
    loading: accessTokenLoading,
    error: accessTokenError,
  } = useAccessToken(APP_ENDPOINT, true);
  const {
    address,
    tenantId,
    doSendEmail,
    loading: addressLoading,
    error: addressError,
    info,
    initInfo,
  } = useAddress(accessToken);
  const {
    hasValidLicense,
    loading: licenseLoading,
    error: licenseError,
  } = useLicense(accessToken, tenantId);
  const { scopes, loading: scopesLoading, error: scopesError } = useScopes(
    accessToken,
  );

  const loading =
    accessTokenLoading || addressLoading || licenseLoading || scopesLoading;
  const error = accessTokenError || addressError || licenseError || scopesError;

  const addedScopes = [...scopes, ...DIRECTORY_SCOPES];
  const sendButtonDisable = loading || error !== "" || !hasValidLicense;

  return (
    <>
      <VerifyForm
        scopes={addedScopes}
        sendButtonDisable={sendButtonDisable}
        address={address}
        doSendEmail={doSendEmail}
      />
      <Loader loaded={!loading} />
      <InfoModal
        show={info !== ""}
        body={info}
        callback={() => {
          initInfo();
        }}
      />
      <ErrorModal
        show={error !== ""}
        body={error}
        callback={() => {
          doAuthorization(APP_ENDPOINT);
        }}
      />
    </>
  );
}

export function useAccessToken(
  redirectUri: string,
  doFetchAccessToken: boolean,
) {
  const [accessToken, setAccessToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchAccessToken();
  }, [doFetchAccessToken]);

  const fetchAccessToken = async () => {
    if (!doFetchAccessToken) {
      return;
    }
    setLoading(true);
    const { result } = parse(window.location.search.substr(1), "&");
    const { code, state } = parse(window.location.hash.substr(1), "&");
    if (code === "" || typeof code !== "string" || state === "") {
      if (result !== "success") {
        try {
          window.location.href = await getAuthorizationUrl(redirectUri);
        } catch (e) {
          setError("doAuthorization:" + JSON.stringify(e.message));
        }
        return;
      }
      doAuthorization(redirectUri);
      return;
    }
    if (state !== sessionStorage.getItem("state")) {
      setError("Invalid state: ");
      return;
    }
    try {
      setAccessToken(await getAccessToken(code, state, redirectUri));
    } catch (e) {
      setError("getAccessToken:" + JSON.stringify(e.message));
    }
    setLoading(false);
  };

  return { accessToken, loading, error };
}

export function useScopes(accessToken: string) {
  const [scopes, setScopes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchScopes();
  }, [accessToken]);

  const fetchScopes = async () => {
    if (!accessToken) {
      return;
    }
    try {
      setLoading(true);
      setScopes(await getOAuthScopes());
    } catch (e) {
      setError(JSON.stringify(e.message));
    }
    setLoading(false);
  };

  return { scopes, loading, error };
}

function useAddress(accessToken: string) {
  const [address, setAddress] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  useEffect(() => {
    fetchAddress();
  }, [accessToken]);

  const fetchAddress = async () => {
    if (!accessToken) {
      return;
    }
    setLoading(true);
    try {
      const { loginMailAddress, tenantId } = await getUserObject(accessToken);
      setAddress(loginMailAddress);
      setTenantId(tenantId);
    } catch (e) {
      setError(`Please login from the user site login page.`);
    }
    setLoading(false);
  };

  const doSendEmail = async () => {
    if (address === "") {
      return;
    }
    try {
      setLoading(true);
      await sendEmail();
      setInfo(
        `Send E-mail to ${address}.\nPlease open E-mail and click the URL.`,
      );
    } catch (e) {
      setInfo(e.message);
    }
    setLoading(false);
  };
  const initInfo = () => {
    setInfo("");
  };

  return { address, tenantId, doSendEmail, loading, error, info, initInfo };
}

function useLicense(accessToken: string, tenantId: string) {
  const [hasValidLicense, setHasLicense] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchLicense();
  }, [accessToken, tenantId]);

  const fetchLicense = async () => {
    if (!accessToken || !tenantId) {
      return;
    }
    setLoading(true);
    try {
      (await getLicenses(accessToken, tenantId)).forEach((license: any) => {
        if (VALID_SERVICE_CLASS.includes(license.serviceClass)) {
          setHasLicense(true);
        }
      });
    } catch (e) {
      setError("useLicense:" + JSON.stringify(e.message));
    }
    setLoading(false);
  };

  return { hasValidLicense, loading, error };
}

export async function getAuthorizationUrl(redirectUri: string) {
  const responseJson = await post(`${SI_API_ENDPOINT}/v1/aut/oidc/rp/request`, {
    body: JSON.stringify({
      opId: "accounts_ricoh_com",
      successUrl: redirectUri,
      failureUrl: redirectUri,
    }),
  });
  if (!responseJson || !responseJson.authorizationUrl) {
    throw Error(responseJson.error);
  }
  return responseJson.authorizationUrl;
}

async function getAccessToken(
  code: string,
  state: string,
  redirectUri: string,
) {
  const responseJson = await post(
    `${SI_API_ENDPOINT}/v1/aut/oauth/provider/token`,
    {
      body: JSON.stringify({
        grant_type: "authorization_code",
        redirect_uri: redirectUri,
        code,
        state,
        code_verifier: sessionStorage.getItem("verifier"),
        client_id: CLIENT_ID,
      }),
    },
  );
  if (!responseJson || !responseJson.access_token) {
    throw Error(responseJson.error);
  }
  return responseJson.access_token;
}

async function getOAuthScopes() {
  const responseJson = await get(
    `${API_ENDPOINT}/appdata/device/user/oauths/google`,
    {},
    "include",
  );
  if (
    responseJson.errors &&
    responseJson.errors[0].message_id === "error.wrong_ticket"
  ) {
    try {
      window.location.href = await getAuthorizationUrl(APP_ENDPOINT);
    } catch (e) {
      throw Error("doAuthorization:" + JSON.stringify(e.message));
    }
  }
  if (!responseJson.requiredScopes) {
    throw Error(responseJson.error);
  }
  return responseJson.requiredScopes;
}

async function getUserObject(accessToken: string) {
  const responseJson = await get(`${SI_API_ENDPOINT}/v1/aut/me`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!responseJson) {
    throw Error(responseJson.error);
  }
  return responseJson;
}

async function getLicenses(accessToken: string, tenantId: string) {
  const responseJson = await get(
    `${SI_API_ENDPOINT}/v1/sem/tenants/${tenantId}/licenses?query[]=status%3Dactive&count=500`,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  if (!responseJson || !responseJson.results) {
    throw Error(responseJson.error);
  }
  return responseJson.results;
}

async function sendEmail() {
  const responseJson = await post(
    `${API_ENDPOINT}/v1/aut/sendGSuiteDomainAdminConfirmation`,
    {
      body: JSON.stringify({
        mailSubject: "Google Workspace Domain Admin Confirmation",
        mailBody: "Click {{url}}",
        successUrl: `${APP_ENDPOINT}/sync`,
        failureUrl: `${APP_ENDPOINT}/sync`,
      }),
    },
    "include",
  );
  if (
    responseJson.errors &&
    responseJson.errors[0].message_id === "error.wrong_ticket"
  ) {
    try {
      window.location.href = await getAuthorizationUrl(APP_ENDPOINT);
    } catch (e) {
      throw Error("doAuthorization:" + JSON.stringify(e.message));
    }
  }
}

export default Verify;
